import React, { useState, useEffect } from 'react';
import { Send, User, Hash, DollarSign, Activity, CheckCircle } from 'lucide-react';
import Swal from 'sweetalert2';

const AddTransaction: React.FC = () => {
  const [customers, setCustomers] = useState<any[]>([]);
  const [isSaving, setIsSaving] = useState(false);
  const [formData, setFormData] = useState({
    user_id: '',
    trx_id: `TRX${Date.now().toString().slice(-6)}`, // Auto generate a random ID initially
    type: 'DEPOSIT',
    amount: '',
    status: 'Success'
  });

  // Fetch all active customers for the dropdown
  useEffect(() => {
    fetch('https://motilalalgo.com/api/admin/customers')
      .then(res => res.json())
      .then(data => {
        if (data.success) {
          // Sirf Active users ko dikhayenge jinka login_id ban gaya hai
          setCustomers(data.users.filter((u: any) => u.login_id));
        }
      });
  }, []);

  const handleInputChange = (e: any) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!formData.user_id || !formData.amount) {
      return Swal.fire('Error', 'Please select a user and enter amount.', 'error');
    }

    setIsSaving(true);
    try {
      const response = await fetch('https://motilalalgo.com/api/admin/add-transaction', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(formData)
      });
      const data = await response.json();
      
      if (data.success) {
        Swal.fire('Success!', 'Transaction recorded in user history.', 'success');
        setFormData({ ...formData, amount: '', trx_id: `TRX${Date.now().toString().slice(-6)}` });
      } else {
        Swal.fire('Error', data.error, 'error');
      }
    } catch (error) {
      Swal.fire('Error', 'Failed to connect to server.', 'error');
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <div className="max-w-4xl mx-auto space-y-6 pb-10">
      <div className="bg-white p-6 rounded-2xl shadow-sm border border-slate-100 mb-6">
        <h1 className="text-2xl font-black text-slate-800">Add New Transaction</h1>
        <p className="text-sm text-slate-500 mt-1">Create a manual transaction. Deposits will automatically add to the user's wallet.</p>
      </div>

      <div className="bg-white rounded-2xl shadow-sm border border-slate-100 p-8">
        <form onSubmit={handleSubmit} className="space-y-6">
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* Select User */}
            <div className="md:col-span-2">
              <label className="block text-xs font-bold text-slate-500 uppercase tracking-widest mb-2 flex items-center gap-2"><User size={14}/> Select Customer</label>
              <select 
                name="user_id" 
                value={formData.user_id} 
                onChange={handleInputChange}
                className="w-full px-4 py-3 bg-slate-50 border border-slate-200 rounded-xl font-bold text-slate-800 outline-none focus:border-[#3b36a4] focus:ring-2 focus:ring-[#3b36a4]/20"
              >
                <option value="">-- Choose a Customer --</option>
                {customers.map(c => (
                  <option key={c.id} value={c.id}>{c.first_name} {c.last_name} (ID: {c.login_id}) - Bal: ₹{c.wallet_balance || 0}</option>
                ))}
              </select>
            </div>

            {/* Transaction ID */}
            <div>
              <label className="block text-xs font-bold text-slate-500 uppercase tracking-widest mb-2 flex items-center gap-2"><Hash size={14}/> Transaction ID / UTR</label>
              <input 
                type="text" 
                name="trx_id"
                value={formData.trx_id}
                onChange={handleInputChange}
                placeholder="e.g. UTR123456789"
                className="w-full px-4 py-3 bg-slate-50 border border-slate-200 rounded-xl font-bold text-slate-800 outline-none focus:border-[#3b36a4] focus:ring-2 focus:ring-[#3b36a4]/20"
              />
            </div>

            {/* Amount */}
            <div>
              <label className="block text-xs font-bold text-slate-500 uppercase tracking-widest mb-2 flex items-center gap-2"><DollarSign size={14}/> Amount (₹)</label>
              <input 
                type="number" 
                name="amount"
                value={formData.amount}
                onChange={handleInputChange}
                placeholder="5000"
                className="w-full px-4 py-3 bg-slate-50 border border-slate-200 rounded-xl font-black text-emerald-600 outline-none focus:border-emerald-500 focus:ring-2 focus:ring-emerald-500/20"
              />
            </div>

            {/* Type */}
            <div>
              <label className="block text-xs font-bold text-slate-500 uppercase tracking-widest mb-2 flex items-center gap-2"><Activity size={14}/> Transaction Type</label>
              <select 
                name="type" 
                value={formData.type} 
                onChange={handleInputChange}
                className="w-full px-4 py-3 bg-slate-50 border border-slate-200 rounded-xl font-bold text-slate-800 outline-none focus:border-[#3b36a4]"
              >
                <option value="DEPOSIT">Deposit (Add Funds)</option>
                <option value="WITHDRAW">Withdraw (Deduct Funds)</option>
              </select>
            </div>

            {/* Status */}
            <div>
              <label className="block text-xs font-bold text-slate-500 uppercase tracking-widest mb-2 flex items-center gap-2"><CheckCircle size={14}/> Status</label>
              <select 
  name="status" 
  value={formData.status} 
  onChange={handleInputChange}
  className="w-full px-4 py-3 bg-slate-50 border border-slate-200 rounded-xl font-bold text-slate-800 outline-none focus:border-[#3b36a4]"
>
  <option value="SUCCESS">Success</option>
  <option value="PENDING">Pending</option>
  <option value="FAILED">Failed</option>
</select>
            </div>
          </div>

          <div className="pt-6 border-t border-slate-100 flex justify-end">
            <button 
              type="submit" 
              disabled={isSaving}
              className="flex items-center gap-2 px-8 py-4 bg-[#3b36a4] text-white font-black rounded-xl shadow-lg hover:bg-[#2d2780] transition-all disabled:opacity-70"
            >
              <Send size={18} /> {isSaving ? 'Processing...' : 'Submit Transaction'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default AddTransaction;